<script setup lang="ts">
import { Form } from "@inertiajs/vue3";
import { useMediaQuery } from "@vueuse/core";
import { FileIcon, ImageIcon, PaperclipIcon, XIcon } from "lucide-vue-next";
import { computed, ref, watch } from "vue";

import MentionEditor from "@/components/mention-editor.vue";
import { Button } from "@/components/ui/button/index";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { mentionedIdsFromText } from "@/lib/discussion-mentions";
import { organizationDiscussionThreadsPath } from "@/routes";
import type { MentionableEntity } from "@/types";

interface Props {
  open: boolean;
  organizationId: number;
  availableTags?: string[];
  mentionables?: MentionableEntity[];
  selectedUserIds?: number[];
  selectedGroupIds?: number[];
  prefillMentionText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  availableTags: () => [],
  mentionables: () => [],
  selectedUserIds: () => [],
  selectedGroupIds: () => [],
  prefillMentionText: "",
});

const emit = defineEmits<{
  "update:open": [value: boolean];
  success: [];
}>();

const isDesktop = useMediaQuery("(min-width: 640px)");
const selectedEmoji = ref("💬");
const description = ref(props.prefillMentionText);
const mentionEditorRef = ref<InstanceType<typeof MentionEditor> | null>(null);
const tagsInput = ref("");

const attachments = ref<File[]>([]);
const fileInputRef = ref<HTMLInputElement | null>(null);
const showDescriptionMentions = ref(false);
const descriptionMentionQuery = ref("");

const Modal = computed(() => ({
  Root: isDesktop.value ? Dialog : Drawer,
  Content: isDesktop.value ? DialogContent : DrawerContent,
  Header: isDesktop.value ? DialogHeader : DrawerHeader,
  Title: isDesktop.value ? DialogTitle : DrawerTitle,
  Description: isDesktop.value ? DialogDescription : DrawerDescription,
  Footer: isDesktop.value ? DialogFooter : DrawerFooter,
  Close: isDesktop.value ? DialogClose : DrawerClose,
}));

const modalRootProps = computed(() =>
  isDesktop.value ? {} : { shouldScaleBackground: false },
);

const filteredMentionables = computed(() => {
  if (!showDescriptionMentions.value) return [];

  const query = descriptionMentionQuery.value.trim();

  return props.mentionables
    .filter((entity) => {
      if (!query) return true;

      const fields = [entity.name.toLowerCase()];
      if (entity.type === "user") {
        fields.push(entity.email.toLowerCase());
      }

      return fields.some((field) => field.includes(query));
    })
    .slice(0, 6);
});

const selectedTargets = computed(() => {
  const users = props.selectedUserIds
    .map((id) => props.mentionables.find((entity) => entity.type === "user" && entity.id === id))
    .filter((entity): entity is MentionableEntity => Boolean(entity));

  const groups = props.selectedGroupIds
    .map((id) => props.mentionables.find((entity) => entity.type === "group" && entity.id === id))
    .filter((entity): entity is MentionableEntity => Boolean(entity));

  return [...users, ...groups];
});

const descriptionMentionIds = computed(() =>
  mentionedIdsFromText(description.value, props.mentionables),
);

const mentionedUserIds = computed(() => descriptionMentionIds.value.userIds);
const mentionedGroupIds = computed(() => descriptionMentionIds.value.groupIds);

const participantUserIds = computed(() =>
  [...new Set([...props.selectedUserIds, ...mentionedUserIds.value])],
);

const participantGroupIds = computed(() =>
  [...new Set([...props.selectedGroupIds, ...mentionedGroupIds.value])],
);

const currentTags = computed(() =>
  tagsInput.value
    .split(/[\s,]+/)
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean),
);

const tagSuggestions = computed(() => {
  const query = tagsInput.value.split(/[\s,]+/).pop()?.trim().toLowerCase() || "";
  return props.availableTags
    .filter((tag) => !currentTags.value.includes(tag.toLowerCase()))
    .filter((tag) => !query || tag.toLowerCase().includes(query))
    .slice(0, 20);
});

const appendTag = (tag: string) => {
  const parts = tagsInput.value
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean);
  const existing = parts.map((value) => value.toLowerCase());

  if (!existing.includes(tag.toLowerCase())) {
    parts.push(tag);
  }

  tagsInput.value = parts.join(", ");
};

const removeTag = (tagToRemove: string) => {
  const parts = tagsInput.value
    .split(/[\s,]+/)
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => v.toLowerCase() !== tagToRemove.toLowerCase());
  tagsInput.value = parts.join(", ");
};

const handleMentionQuery = (query: string) => {
  descriptionMentionQuery.value = query;
  showDescriptionMentions.value = true;
};

const handleMentionClear = () => {
  showDescriptionMentions.value = false;
  descriptionMentionQuery.value = "";
};

const handleDescriptionMentionSelect = (entity: MentionableEntity) => {
  mentionEditorRef.value?.insertMention(entity);
};

const openFilePicker = () => {
  if (!fileInputRef.value) return;
  fileInputRef.value.value = "";
  fileInputRef.value.click();
};

const handleFileSelection = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selectedFiles = target.files ? Array.from(target.files) : [];
  if (selectedFiles.length === 0) return;
  attachments.value = [...attachments.value, ...selectedFiles];
  syncInputFiles();
};

const removeAttachment = (index: number) => {
  attachments.value = attachments.value.filter((_, i) => i !== index);
  syncInputFiles();
};

const syncInputFiles = () => {
  if (!fileInputRef.value || typeof DataTransfer === "undefined") return;
  const dt = new DataTransfer();
  attachments.value.forEach((file) => dt.items.add(file));
  fileInputRef.value.files = dt.files;
};

const handleOpenChange = (value: boolean) => {
  emit("update:open", value);
};

const formKey = ref(0);

const handleSuccess = () => {
  selectedEmoji.value = "💬";
  description.value = props.prefillMentionText;
  tagsInput.value = "";
  attachments.value = [];
  showDescriptionMentions.value = false;
  descriptionMentionQuery.value = "";
  formKey.value++;
  emit("update:open", false);
  emit("success");
};

watch(
  () => props.prefillMentionText,
  (nextValue) => {
    description.value = nextValue;
  },
);
</script>

<template>
  <component :is="Modal.Root" v-bind="modalRootProps" :open="open" @update:open="handleOpenChange">
    <component
      :is="Modal.Content"
      class="sm:max-w-lg"
      :class="[{ 'flex max-h-[85dvh] flex-col overflow-hidden px-2 pb-4 *:px-4': !isDesktop }]"
    >
      <component :is="Modal.Header">
        <component :is="Modal.Title" class="text-lg font-semibold">Nuevo hilo</component>
        <component :is="Modal.Description" class="text-sm text-muted-foreground">
          Abre una conversación para el equipo.
        </component>
      </component>

      <Form
        :key="formKey"
        :action="organizationDiscussionThreadsPath(props.organizationId)"
        method="post"
        :options="{ forceFormData: true }"
        class="flex min-h-0 flex-1 flex-col"
        @success="handleSuccess"
        #default="{ errors, processing }"
      >
        <div
          :class="isDesktop ? 'space-y-4' : 'themed-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto pr-1'"
        >
          <!-- Participantes preseleccionados -->
          <div
            v-if="selectedTargets.length > 0"
            class="flex flex-wrap gap-1.5 rounded-lg bg-muted/50 px-3 py-2.5"
          >
            <span class="mr-1 text-xs text-muted-foreground">Para:</span>
            <span
              v-for="entity in selectedTargets"
              :key="`selected-${entity.type}-${entity.id}`"
              class="rounded-full bg-background px-2 py-0.5 text-xs font-medium shadow-sm"
            >
              @{{ entity.name }}
            </span>
          </div>

          <!-- Título -->
          <div>
            <Input
              id="discussion-title"
              name="discussion_thread[title]"
              type="text"
              required
              placeholder="¿Qué tema necesitan resolver?"
              class="h-12 text-base font-medium sm:h-11 sm:text-sm"
            />
            <p
              v-if="errors['discussion_thread.title'] || errors.title"
              class="mt-1 text-sm text-destructive"
            >
              {{ errors["discussion_thread.title"] || errors.title }}
            </p>
          </div>

          <input type="hidden" name="discussion_thread[emoji]" :value="selectedEmoji" />
          <input
            v-for="userId in participantUserIds"
            :key="`participant-user-${userId}`"
            type="hidden"
            name="discussion_thread[participant_user_ids][]"
            :value="userId"
          />
          <input
            v-for="groupId in participantGroupIds"
            :key="`participant-group-${groupId}`"
            type="hidden"
            name="discussion_thread[participant_group_ids][]"
            :value="groupId"
          />
          <input
            v-for="userId in mentionedUserIds"
            :key="`mentioned-user-${userId}`"
            type="hidden"
            name="discussion_thread[mentioned_user_ids][]"
            :value="userId"
          />
          <input
            v-for="groupId in mentionedGroupIds"
            :key="`mentioned-group-${groupId}`"
            type="hidden"
            name="discussion_thread[mentioned_group_ids][]"
            :value="groupId"
          />

          <!-- Descripción -->
          <div>
            <div class="relative">
              <div
                v-if="showDescriptionMentions && filteredMentionables.length > 0"
                class="absolute bottom-full left-0 right-0 z-10 mb-1 overflow-hidden rounded-lg border bg-popover shadow-md"
              >
                <ul class="max-h-44 overflow-y-auto py-1">
                  <li
                    v-for="entity in filteredMentionables"
                    :key="`${entity.type}-${entity.id}`"
                  >
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-muted/60"
                      @mousedown.prevent
                      @click="handleDescriptionMentionSelect(entity)"
                    >
                      <span class="truncate font-medium">@{{ entity.name }}</span>
                      <span v-if="entity.type === 'user'" class="ml-auto truncate text-xs text-muted-foreground">
                        {{ entity.email }}
                      </span>
                      <span v-else class="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        Grupo
                      </span>
                    </button>
                  </li>
                </ul>
              </div>

              <MentionEditor
                ref="mentionEditorRef"
                v-model="description"
                :mentionables="props.mentionables"
                placeholder="Agrega contexto (opcional). Usa @ para mencionar."
                @mention-query="handleMentionQuery"
                @mention-clear="handleMentionClear"
              />
            </div>
            <p
              v-if="errors['discussion_thread.description'] || errors.description"
              class="mt-1 text-sm text-destructive"
            >
              {{ errors["discussion_thread.description"] || errors.description }}
            </p>
          </div>

          <!-- Tags y Fecha en una fila -->
          <div class="grid gap-3 sm:grid-cols-2">
            <div>
              <label for="discussion-tags" class="mb-1.5 block text-sm font-medium">
                Tema <span class="font-normal text-muted-foreground">(opcional)</span>
              </label>
              <input
                id="discussion-tags"
                name="discussion_thread[tags]"
                v-model="tagsInput"
                placeholder="Escribe o elegí un tema..."
                class="border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[3px]"
              >

              <!-- Tags seleccionados -->
              <div v-if="currentTags.length > 0" class="mt-2 flex flex-wrap gap-1.5">
                <button
                  v-for="tag in currentTags"
                  :key="`current-${tag}`"
                  type="button"
                  class="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs font-medium transition hover:bg-muted"
                  @click="removeTag(tag)"
                >
                  #{{ tag }}
                  <XIcon class="size-3 text-muted-foreground" />
                </button>
              </div>

              <!-- Sugerencias (filtrables, scrollables) -->
              <div
                v-if="tagSuggestions.length > 0"
                class="mt-2 flex max-h-24 flex-wrap gap-1 overflow-y-auto"
              >
                <button
                  v-for="tag in tagSuggestions"
                  :key="tag"
                  type="button"
                  class="rounded-full border border-dashed border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground transition hover:border-border hover:bg-muted/40 hover:text-foreground"
                  @click="appendTag(tag)"
                >
                  {{ tag }}
                </button>
              </div>

              <p
                v-if="errors['discussion_thread.tags'] || errors.tags"
                class="mt-1 text-sm text-destructive"
              >
                {{ errors["discussion_thread.tags"] || errors.tags }}
              </p>
            </div>

            <div>
              <label for="discussion-due-date" class="mb-1.5 block text-sm font-medium">
                Fecha límite <span class="font-normal text-muted-foreground">(opcional)</span>
              </label>
              <Input
                id="discussion-due-date"
                name="discussion_thread[due_date]"
                type="date"
                class="h-10"
              />
              <p
                v-if="errors['discussion_thread.due_date'] || errors.due_date"
                class="mt-1 text-sm text-destructive"
              >
                {{ errors["discussion_thread.due_date"] || errors.due_date }}
              </p>
            </div>
          </div>

          <!-- Archivos -->
          <div>
            <div v-if="attachments.length > 0" class="mb-2 flex flex-wrap gap-2">
              <div
                v-for="(file, index) in attachments"
                :key="`${file.name}-${index}`"
                class="flex items-center gap-1.5 rounded-md border bg-muted/30 px-2.5 py-1.5"
              >
                <component
                  :is="file.type.startsWith('image/') ? ImageIcon : FileIcon"
                  class="size-3.5 text-muted-foreground"
                />
                <span class="max-w-[140px] truncate text-xs">{{ file.name }}</span>
                <button
                  type="button"
                  class="rounded p-0.5 text-muted-foreground transition hover:text-foreground"
                  @click="removeAttachment(index)"
                >
                  <XIcon class="size-3" />
                </button>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              class="h-8 text-muted-foreground"
              @click="openFilePicker"
            >
              <PaperclipIcon class="mr-1.5 size-3.5" />
              Adjuntar archivos
            </Button>
            <input
              ref="fileInputRef"
              type="file"
              name="discussion_thread[attachments][]"
              multiple
              class="hidden"
              @change="handleFileSelection"
            />
          </div>
        </div>

        <component :is="Modal.Footer" class="mt-5 shrink-0 flex-col-reverse gap-2 sm:flex-row">
          <component :is="Modal.Close" as-child>
            <Button variant="ghost" class="h-10 w-full sm:w-auto">
              Cancelar
            </Button>
          </component>
          <Button
            type="submit"
            :disabled="processing"
            class="h-10 w-full sm:w-auto"
          >
            {{ processing ? "Creando..." : "Crear hilo" }}
          </Button>
        </component>
      </Form>
    </component>
  </component>
</template>
